import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Check,
  ChevronRight,
  RotateCcw,
  ShieldAlert,
  X,
} from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { PageHeader } from "../components/PageHeader";
import { RetryButton, StatePanel } from "../components/StatePanel";
import { StatusChip } from "../components/StatusChip";
import { api, formatDate, mutationKey, protectedBlob } from "../lib/api";
import type { ListResponse, ReviewItem } from "../lib/types";

function formText(data: FormData, name: string): string {
  const value = data.get(name);
  return typeof value === "string" ? value : "";
}

interface ReviewDetail {
  readonly post: Readonly<Record<string, unknown>> & {
    readonly id: string;
    readonly media_id: string;
    readonly condition: string;
    readonly location_status: string;
    readonly resource_version: number;
  };
  readonly timeline: readonly Readonly<Record<string, unknown>>[];
  readonly risk_flags: readonly {
    readonly code: string;
    readonly severity: string;
    readonly explanation: string;
  }[];
  readonly allowance_history: readonly Readonly<Record<string, unknown>>[];
  readonly observations: readonly Readonly<Record<string, unknown>>[];
  readonly maintenance: readonly Readonly<Record<string, unknown>>[];
  readonly revisions: readonly {
    readonly id: string;
    readonly revision_number: number;
    readonly correction_note: string;
    readonly created_at: string;
  }[];
  readonly projected_points: {
    readonly total: number;
    readonly entries: readonly {
      readonly ruleCode: string;
      readonly amount: number;
      readonly explanation: string;
    }[];
    readonly evaluations: readonly {
      readonly ruleCode: string;
      readonly amount: number;
      readonly eligible: boolean;
      readonly explanation: string;
      readonly cooldownDays?: number;
      readonly cooldownUntil?: string;
    }[];
  };
  readonly conflict_blocked: boolean;
}

export function ReviewsPage() {
  const [selected, setSelected] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get("post"),
  );
  const [filters, setFilters] = useState({
    condition: "",
    municipality_id: "",
    site_id: "",
    manager_id: "",
    post_type: "",
    state: "pending",
    risk_type: "",
    age_days: "",
  });
  const reference = useQuery({
    queryKey: ["reference"],
    queryFn: () =>
      api<{
        readonly municipalities: readonly {
          readonly id: string;
          readonly name: string;
        }[];
        readonly sites: readonly {
          readonly id: string;
          readonly name: string;
        }[];
      }>("/reference-data"),
  });
  const search = new URLSearchParams(
    Object.entries(filters).filter(([, value]) => value !== ""),
  );
  const queue = useQuery({
    queryKey: ["reviews", filters],
    queryFn: () =>
      api<ListResponse<ReviewItem>>(`/reviews?${search.toString()}`),
  });
  return (
    <>
      <PageHeader
        eyebrow="Verifier workspace"
        title="Evidence review"
        description="Review age, field condition, location result, and risk signals before deciding."
      />
      <div className="filter-bar">
        <label>
          Condition
          <select
            value={filters.condition}
            onChange={(event) =>
              setFilters({ ...filters, condition: event.target.value })
            }
          >
            <option value="">All conditions</option>
            <option value="healthy">Healthy</option>
            <option value="needs_care">Needs care</option>
            <option value="at_risk">At risk</option>
            <option value="dead">Dead</option>
          </select>
        </label>
        <label>
          Review state
          <select
            value={filters.state}
            onChange={(event) =>
              setFilters({ ...filters, state: event.target.value })
            }
          >
            <option value="pending">Pending</option>
            <option value="in_review">In review</option>
            <option value="returned">Returned</option>
            <option value="resubmitted">Resubmitted</option>
            <option value="verified">Verified</option>
            <option value="rejected">Rejected</option>
          </select>
        </label>
        <label>
          Municipality
          <select
            value={filters.municipality_id}
            onChange={(event) =>
              setFilters({ ...filters, municipality_id: event.target.value })
            }
          >
            <option value="">All municipalities</option>
            {reference.data?.municipalities.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Site
          <select
            value={filters.site_id}
            onChange={(event) =>
              setFilters({ ...filters, site_id: event.target.value })
            }
          >
            <option value="">All sites</option>
            {reference.data?.sites.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Post type
          <select
            value={filters.post_type}
            onChange={(event) =>
              setFilters({ ...filters, post_type: event.target.value })
            }
          >
            <option value="">All types</option>
            <option value="original">Original</option>
            <option value="follow_up">Follow-up</option>
          </select>
        </label>
        <label>
          Submission age
          <select
            value={filters.age_days}
            onChange={(event) =>
              setFilters({ ...filters, age_days: event.target.value })
            }
          >
            <option value="">Any age</option>
            <option value="1">Older than 1 day</option>
            <option value="3">Older than 3 days</option>
            <option value="7">Older than 7 days</option>
          </select>
        </label>
        <label>
          Risk type
          <input
            value={filters.risk_type}
            onChange={(event) =>
              setFilters({ ...filters, risk_type: event.target.value })
            }
            placeholder="e.g. location_outside"
          />
        </label>
        <label>
          Manager ID
          <input
            value={filters.manager_id}
            onChange={(event) =>
              setFilters({ ...filters, manager_id: event.target.value })
            }
          />
        </label>
      </div>
      {queue.isLoading ? (
        <StatePanel
          kind="loading"
          title="Loading the review queue"
          detail="Prioritizing age and risk within your assigned scope."
        />
      ) : queue.isError || queue.data === undefined ? (
        <StatePanel
          kind="error"
          title="The queue is unavailable"
          detail="No review decision was changed."
          action={<RetryButton onClick={() => void queue.refetch()} />}
        />
      ) : queue.data.items.length === 0 ? (
        <StatePanel
          kind="empty"
          title="Queue clear"
          detail="There is no pending evidence in the current filter."
        />
      ) : (
        <div className="review-layout">
          <section className="review-list" aria-label="Review queue">
            {queue.data.items.map((item) => (
              <button
                key={item.id}
                type="button"
                className={selected === item.id ? "selected" : undefined}
                onClick={() => setSelected(item.id)}
              >
                <span className="review-thumb" aria-hidden="true">
                  <span>{item.plant_type.slice(0, 1)}</span>
                </span>
                <span className="review-summary">
                  <span>
                    <strong>{item.plant_type}</strong>
                    <StatusChip state={item.condition} />
                  </span>
                  <small>
                    {item.site_name} · {item.post_type.replaceAll("_", " ")}
                  </small>
                  <small>
                    {formatDate(item.uploaded_at)} · Location{" "}
                    {item.location_status}
                  </small>
                  {item.risk_flags === null ? null : (
                    <em>
                      <AlertTriangle aria-hidden="true" />
                      {item.risk_flags.replaceAll(",", ", ")}
                    </em>
                  )}
                </span>
                <ChevronRight aria-hidden="true" />
              </button>
            ))}
          </section>
          <ReviewDetailPanel postId={selected} />
        </div>
      )}
    </>
  );
}

function ReviewDetailPanel({ postId }: { readonly postId: string | null }) {
  const queryClient = useQueryClient();
  const [pendingDecision, setPendingDecision] = useState<
    "approved" | "returned" | "rejected" | "escalated" | null
  >(null);
  const detail = useQuery({
    queryKey: ["review-detail", postId],
    queryFn: () => api<ReviewDetail>(`/reviews/${postId ?? ""}`),
    enabled: postId !== null,
  });
  const decision = useMutation({
    mutationFn: ({
      value,
      reason,
      rejectCategory,
      note,
    }: {
      readonly value: "approved" | "returned" | "rejected" | "escalated";
      readonly reason: string;
      readonly rejectCategory?: string;
      readonly note?: string;
    }) =>
      api(`/reviews/${postId ?? ""}/decision`, {
        method: "POST",
        idempotencyKey: mutationKey("review"),
        body: JSON.stringify({
          decision: value,
          reason_code: reason,
          ...(value === "rejected"
            ? { reject_category: rejectCategory ?? "other" }
            : {}),
          note: note ?? "",
          expected_resource_version: detail.data?.post.resource_version ?? 1,
        }),
      }),
    onSuccess: async () => {
      setPendingDecision(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["reviews"] }),
        queryClient.invalidateQueries({ queryKey: ["review-detail", postId] }),
      ]);
    },
  });
  if (postId === null) {
    return (
      <StatePanel
        kind="empty"
        title="Select evidence"
        detail="Choose a queue item to compare the original, prior updates, and projected outcome."
      />
    );
  }
  if (detail.isLoading) {
    return (
      <StatePanel
        kind="loading"
        title="Loading evidence"
        detail="Opening authorized location and planting history."
      />
    );
  }
  if (detail.isError || detail.data === undefined) {
    return (
      <StatePanel
        kind="error"
        title="Evidence unavailable"
        detail="The item could not be opened. No decision was changed."
        action={<RetryButton onClick={() => void detail.refetch()} />}
      />
    );
  }
  const data = detail.data;
  return (
    <section className="review-detail">
      <ProtectedEvidenceImage mediaId={data.post.media_id} />
      <div className="evidence-heading">
        <div>
          <p className="eyebrow">Current evidence</p>
          <h2>{data.post.condition.replaceAll("_", " ")}</h2>
        </div>
        <StatusChip state={data.post.location_status} />
      </div>
      <dl className="evidence-facts">
        <div>
          <dt>Captured</dt>
          <dd>{formatDate(String(data.post.captured_at))}</dd>
        </div>
        <div>
          <dt>Uploaded</dt>
          <dd>{formatDate(String(data.post.uploaded_at))}</dd>
        </div>
        <div>
          <dt>Observed</dt>
          <dd>{String(data.post.observed_plant_count)} plants</dd>
        </div>
        <div>
          <dt>Accuracy</dt>
          <dd>{String(data.post.accuracy_m)} m</dd>
        </div>
      </dl>
      <section className="private-location">
        <p className="eyebrow">Authorized exact location</p>
        <div
          className="evidence-map"
          role="img"
          aria-label="Exact evidence map"
        >
          <span aria-hidden="true">●</span>
        </div>
        <code>
          {String(data.post.latitude)}, {String(data.post.longitude)}
        </code>
        <small>This access is recorded in the audit log.</small>
      </section>
      {data.risk_flags.length > 0 ? (
        <div className="risk-box">
          <ShieldAlert aria-hidden="true" />
          <div>
            <strong>Review signals, not a decision</strong>
            {data.risk_flags.map((flag) => (
              <p key={flag.code}>{flag.explanation}</p>
            ))}
          </div>
        </div>
      ) : null}
      <section className="outcome-preview">
        <p className="eyebrow">Approval outcome</p>
        <h3>{data.projected_points.total} verified points</h3>
        {data.projected_points.evaluations.map((evaluation) => (
          <div className="point-evaluation" key={evaluation.ruleCode}>
            <span>
              <strong>
                {evaluation.eligible ? "Eligible" : "Not eligible"}:{" "}
                {evaluation.ruleCode.replaceAll("_", " ")}
              </strong>
              <small>{evaluation.explanation}</small>
              {evaluation.cooldownDays !== undefined ? (
                <small>
                  Cooldown: {evaluation.cooldownDays} days
                  {evaluation.cooldownUntil === undefined
                    ? ""
                    : ` · ends ${formatDate(evaluation.cooldownUntil)}`}
                </small>
              ) : null}
            </span>
            <strong>
              {evaluation.eligible ? `+${evaluation.amount}` : "0"}
            </strong>
          </div>
        ))}
        {data.projected_points.evaluations.length === 0 ? (
          <p>No active point rule applies to this evidence.</p>
        ) : null}
        <small>One assignment slot was already used at submission.</small>
      </section>
      <section>
        <p className="eyebrow">Connected timeline</p>
        <h3>{data.timeline.length} evidence record(s)</h3>
        <ol className="timeline-list">
          {data.timeline.map((item) => (
            <li key={String(item.id)}>
              <strong>{String(item.post_type).replaceAll("_", " ")}</strong>
              <span>
                {formatDate(String(item.captured_at))} ·{" "}
                {String(item.condition)}
              </span>
            </li>
          ))}
        </ol>
      </section>
      {data.revisions.length > 0 ? (
        <section>
          <p className="eyebrow">Correction history</p>
          <h3>{data.revisions.length} submitted revision(s)</h3>
          <ol className="timeline-list">
            {data.revisions.map((revision) => (
              <li key={revision.id}>
                <strong>Revision {revision.revision_number}</strong>
                <span>
                  {revision.correction_note} · {formatDate(revision.created_at)}
                </span>
              </li>
            ))}
          </ol>
        </section>
      ) : null}
      <section className="evidence-columns">
        <div>
          <p className="eyebrow">Measurements & maintenance</p>
          {data.observations.map((item) => (
            <p key={String(item.metric_code)}>
              {String(item.metric_code).replaceAll("_", " ")}:{" "}
              {String(item.numeric_value)} {String(item.unit)}
            </p>
          ))}
          <p>
            {data.maintenance
              .map((item) => String(item.action_code))
              .join(", ") || "No maintenance recorded"}
          </p>
        </div>
        <div>
          <p className="eyebrow">Allowance history</p>
          {data.allowance_history.map((item) => (
            <p key={String(item.id)}>
              {String(item.entry_kind).replaceAll("_", " ")} ·{" "}
              {String(item.quantity)}
            </p>
          ))}
        </div>
      </section>
      {data.conflict_blocked ? (
        <StatePanel
          kind="permission"
          title="Conflict prevents a decision"
          detail="You authored this post. Another assigned verifier must decide it."
        />
      ) : (
        <div className="decision-bar" aria-live="polite">
          <button
            className="button primary"
            type="button"
            disabled={decision.isPending}
            onClick={() => setPendingDecision("approved")}
          >
            <Check aria-hidden="true" />
            Approve
          </button>
          <button
            className="button secondary"
            type="button"
            disabled={decision.isPending}
            onClick={() => setPendingDecision("returned")}
          >
            <RotateCcw aria-hidden="true" />
            Return
          </button>
          <button
            className="button ghost-danger"
            type="button"
            disabled={decision.isPending}
            onClick={() => setPendingDecision("rejected")}
          >
            <X aria-hidden="true" />
            Reject
          </button>
          <button
            className="button secondary"
            type="button"
            disabled={decision.isPending}
            onClick={() => setPendingDecision("escalated")}
          >
            <ShieldAlert aria-hidden="true" />
            Escalate
          </button>
        </div>
      )}
      {decision.isError ? (
        <p className="form-error" role="alert">
          {decision.error.message}
        </p>
      ) : null}
      {pendingDecision === null ? null : (
        <ReviewDecisionDialog
          value={pendingDecision}
          projectedPoints={data.projected_points.total}
          pending={decision.isPending}
          error={decision.isError ? decision.error.message : null}
          onCancel={() => setPendingDecision(null)}
          onConfirm={(input) => decision.mutate(input)}
        />
      )}
    </section>
  );
}

const returnReasons = [
  "repeated_angle",
  "low_light",
  "planting_not_visible",
  "wider_view_required",
  "location_unclear",
  "quantity_mismatch",
  "measurement_correction",
] as const;

const rejectCategories = [
  "fraud",
  "privacy",
  "unrelated_content",
  "unsafe_content",
  "ineligible",
  "other",
] as const;

function ReviewDecisionDialog({
  value,
  projectedPoints,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  readonly value: "approved" | "returned" | "rejected" | "escalated";
  readonly projectedPoints: number;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onCancel: () => void;
  readonly onConfirm: (input: {
    readonly value: "approved" | "returned" | "rejected" | "escalated";
    readonly reason: string;
    readonly rejectCategory?: string;
    readonly note?: string;
  }) => void;
}) {
  const label = {
    approved: "approval",
    returned: "return",
    rejected: "rejection",
    escalated: "escalation",
  }[value];
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    if (value === "approved") {
      onConfirm({ value, reason: "evidence_sufficient" });
      return;
    }
    if (value === "returned") {
      onConfirm({
        value,
        reason: formText(data, "return_reason"),
        note: formText(data, "note").trim(),
      });
      return;
    }
    const note = formText(data, "note").trim();
    if (value === "rejected") {
      const rejectCategory = formText(data, "reject_category");
      onConfirm({
        value,
        reason: `rejected_${rejectCategory}`,
        rejectCategory,
        note,
      });
      return;
    }
    onConfirm({ value, reason: "senior_review_required", note });
  };
  return (
    <div className="dialog-layer" role="presentation">
      <form
        className="dialog review-decision-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="review-decision-title"
        aria-describedby="review-decision-impact"
        onSubmit={submit}
        onKeyDown={(event) => {
          if (event.key === "Escape" && !pending) onCancel();
        }}
      >
        <div className="dialog-title">
          <div>
            <p className="eyebrow">Decision confirmation</p>
            <h2 id="review-decision-title">Confirm {label}</h2>
          </div>
          <button
            className="icon-button"
            type="button"
            aria-label="Close decision dialog"
            disabled={pending}
            autoFocus={value === "approved"}
            onClick={onCancel}
          >
            <X aria-hidden="true" />
          </button>
        </div>
        <p id="review-decision-impact">
          {value === "approved"
            ? `${projectedPoints.toString()} verified points will be posted using the previewed rules.`
            : value === "returned"
              ? "The planter can correct this same post without using another allowance slot."
              : value === "rejected"
                ? "The evidence will not appear publicly and the reason remains in review history."
                : "The post stays under review for a senior verifier."}
        </p>
        {value === "returned" ? (
          <label>
            Return reason
            <select
              name="return_reason"
              defaultValue="wider_view_required"
              autoFocus
            >
              {returnReasons.map((reason) => (
                <option key={reason} value={reason}>
                  {reason.replaceAll("_", " ")}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {value === "rejected" ? (
          <label>
            Reject category
            <select name="reject_category" defaultValue="ineligible" autoFocus>
              {rejectCategories.map((category) => (
                <option key={category} value={category}>
                  {category.replaceAll("_", " ")}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {value === "approved" ? null : (
          <label>
            {value === "returned" ? "Reviewer note (optional)" : "Reason"}
            <textarea
              name="note"
              required={value !== "returned"}
              minLength={value === "returned" ? undefined : 3}
              maxLength={1000}
              autoFocus={value === "escalated"}
            />
          </label>
        )}
        {error === null ? null : (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div className="dialog-actions">
          <button
            className="button secondary"
            type="button"
            disabled={pending}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button className="button primary" type="submit" disabled={pending}>
            {pending ? "Saving decision…" : `Confirm ${label}`}
          </button>
        </div>
      </form>
    </div>
  );
}

function ProtectedEvidenceImage({ mediaId }: { readonly mediaId: string }) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const evidence = useQuery({
    queryKey: ["review-media", mediaId],
    queryFn: () => protectedBlob(`/media/${mediaId}?size=display`, "image/*"),
  });
  useEffect(() => {
    if (
      evidence.data === undefined ||
      typeof URL.createObjectURL !== "function"
    ) {
      setObjectUrl(null);
      return;
    }
    const nextUrl = URL.createObjectURL(evidence.data);
    setObjectUrl(nextUrl);
    return () => URL.revokeObjectURL(nextUrl);
  }, [evidence.data]);
  if (
    evidence.isLoading ||
    (evidence.data !== undefined &&
      objectUrl === null &&
      typeof URL.createObjectURL === "function")
  ) {
    return (
      <div className="evidence-image evidence-image-state" role="status">
        Loading protected evidence…
      </div>
    );
  }
  if (evidence.isError || objectUrl === null) {
    return (
      <div className="evidence-image evidence-image-state" role="alert">
        <span>Protected evidence could not be displayed.</span>
        <button
          className="text-button"
          type="button"
          onClick={() => void evidence.refetch()}
        >
          Try again
        </button>
      </div>
    );
  }
  return (
    <div className="evidence-image">
      <img src={objectUrl} alt="Planting evidence submitted for verification" />
    </div>
  );
}
